// Losses, consumption and the accuracy of the picture you were fighting from. Every figure carries one
// sentence of plain language, because a number without an interpretation is not a lesson.

import type { RunStats } from './format';
import { Heading } from './Logs';

/** "0 destroyed · 5 broken": the two costs stated apart, because they are not the same cost. */
export function lossesLine(destroyed: number, broken: number): string {
  return `${destroyed} destroyed · ${broken} broken`;
}

/** What a picture statistic says when there was no picture at all. */
const NO_CONTACTS = 'no contacts were reported in this run, so there is nothing to measure the picture against.';

export function Stats({ stats }: { stats: RunStats }) {
  const typeRate = stats.typeRate === null ? null : Math.round(stats.typeRate * 100);

  return (
    <section data-testid="stats" className="wt-panel p-3">
      <Heading>Losses, consumption and the accuracy of your picture</Heading>
      <div className="mt-2 grid grid-cols-2 gap-2 lg:grid-cols-3">
        <Stat
          label="Your losses"
          value={lossesLine(stats.blueLosses, stats.blueBroken)}
          tone="var(--color-friendly)"
          note={stats.blueLosses === 0 && stats.blueBroken === 0
            ? `You finished the run with all ${stats.blueTotal} of your units still fighting.`
            : `Of your ${stats.blueTotal} units, ${stats.blueLosses} were destroyed outright and ` +
              `${stats.blueBroken} broke — combat ineffective, still on the map, out of the fight.`}
        />
        <Stat
          label="Enemy losses"
          value={lossesLine(stats.redLosses, stats.redBroken)}
          tone="var(--color-hostile)"
          note={stats.redLosses === 0 && stats.redBroken === 0
            ? 'You destroyed nothing and broke nothing: the enemy force is intact and still in position.'
            : `Of the ${stats.redTotal} enemy units on the ground, you destroyed ${stats.redLosses} ` +
              `and broke ${stats.redBroken}.`}
        />
        <Stat
          label="Average strength"
          value={`${Math.round(stats.strengthPct)}%`}
          tone="var(--color-paper)"
          note="Combat power left in your ground platoons at the end of the run."
        />
        <Stat
          label="Fuel remaining"
          value={`${Math.round(stats.fuelPct)}%`}
          tone={stats.fuelPct < 35 ? 'var(--color-warn)' : 'var(--color-paper)'}
          note={stats.fuelPct < 35
            ? 'Low: the force was close to culminating on fuel and could not have pressed much further.'
            : 'The force still had the fuel to continue the attack.'}
        />
        <Stat
          label="Ammunition remaining"
          value={`${Math.round(stats.ammoPct)}%`}
          tone={stats.ammoPct < 35 ? 'var(--color-warn)' : 'var(--color-paper)'}
          note={stats.ammoPct < 35
            ? 'Low: another engagement would have needed a resupply first.'
            : 'The force could still fight a full engagement.'}
        />
        <Stat
          label="Duration"
          value={`${Math.round(stats.durationMin)} min`}
          tone="var(--color-paper)"
          note="Simulated time from H-hour to the end of the run."
        />
        <Stat
          label="Contacts typed correctly"
          value={typeRate === null ? '—' : `${stats.contactsCorrectType} / ${stats.contacts}`}
          tone={typeRate === null ? 'var(--color-fog)' : typeRate >= 60 ? 'var(--color-isr)' : 'var(--color-warn)'}
          note={typeRate === null
            ? `No contacts, so ${NO_CONTACTS}`
            : `${typeRate}% of the enemy units your observers reported were called as the right kind of unit; the rest you fought on a guess.`}
        />
        <Stat
          label="Mean position error"
          value={stats.meanPositionErrorM === null ? '—' : `${Math.round(stats.meanPositionErrorM)} m`}
          tone={stats.meanPositionErrorM === null
            ? 'var(--color-fog)'
            : stats.meanPositionErrorM > 500 ? 'var(--color-warn)' : 'var(--color-isr)'}
          note={stats.meanPositionErrorM === null
            ? `A mean error of 0 m would read as a perfect picture; ${NO_CONTACTS}`
            : stats.meanPositionErrorM < 1
              ? `Your observers plotted contacts on the ground they were actually looking at, so the dot was right — but every report came with an uncertainty of ±${Math.round(stats.meanReportedErrorM)} m on average, and that ring, not the dot, was the real limit of your picture.`
              : `On average your reported enemy positions were ${Math.round(stats.meanPositionErrorM)} m from where the enemy actually was when the report came in, against a quoted uncertainty of ±${Math.round(stats.meanReportedErrorM)} m.`}
        />
        <Stat
          label="Enemies never seen"
          value={`${stats.enemiesNeverSeen}`}
          tone={stats.enemiesNeverSeen > 0 ? 'var(--color-hostile)' : 'var(--color-isr)'}
          note={stats.enemiesNeverSeen > 0
            ? `${stats.enemiesNeverSeen} enemy ${stats.enemiesNeverSeen === 1 ? 'unit was' : 'units were'} never reported at all — you fought the whole run without knowing ${stats.enemiesNeverSeen === 1 ? 'it was' : 'they were'} there.`
            : 'Every enemy unit on the ground was reported at least once.'}
        />
      </div>
    </section>
  );
}

function Stat({ label, value, note, tone }: { label: string; value: string; note: string; tone: string }) {
  return (
    <article data-testid="stat" className="wt-card">
      <div className="mono text-[9px] uppercase tracking-[0.14em] text-fog">{label}</div>
      <div className="mono mt-0.5 text-[18px]" style={{ color: tone }}>{value}</div>
      <p className="mt-1 text-[11px] leading-snug text-fog">{note}</p>
    </article>
  );
}
